import { isDisplayUnit } from './unitCategories.js';

/**
 * Resolve the unit_id that new writes should target.
 *
 * For non-display units this is just `unit.unit_id`. For display units we
 * pick the range marked `current === true`. Returns `undefined` when no
 * suitable range is found, mirroring the original page-local helper.
 */
export function getCurrentWritableUnitId(unit) {
    if (!unit) return undefined;
    if (!isDisplayUnit(unit)) return unit.unit_id;
    const currentRange = unit.unit_ranges.find((range) => range?.current === true);
    return currentRange?.unit_id;
}

export function isUnitRangeActive(range, nowMs = Date.now()) {
    if (!range || !range.unit_id) return false;
    if (range.current === true) return true;

    const start = Number(range.start_date);
    const end = Number(range.end_date);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;

    const timestamp = Number(nowMs);
    if (!Number.isFinite(timestamp)) return false;

    return timestamp >= start && timestamp <= end;
}

export function getActiveUnitIdsForDisplayUnit(displayUnit, nowMs = Date.now()) {
    if (!displayUnit) return [];
    if (!isDisplayUnit(displayUnit)) {
        return displayUnit.unit_id ? [displayUnit.unit_id] : [];
    }

    const unitIds = displayUnit.unit_ranges
        .filter((range) => isUnitRangeActive(range, nowMs))
        .map((range) => range.unit_id)
        .filter(Boolean);

    return [...new Set(unitIds)];
}

export function getLocationUnitNavigationTargets(location, nowMs = Date.now()) {
    if (!location) return [];

    if (Array.isArray(location.display_units)) {
        const targets = [];
        location.display_units.forEach((displayUnit, displayUnitIndex) => {
            getActiveUnitIdsForDisplayUnit(displayUnit, nowMs).forEach((unitId) => {
                targets.push({
                    type: 'display',
                    unitId,
                    targetId: displayUnitIndex,
                    displayUnitIndex,
                    displayUnit,
                });
            });
        });
        return targets;
    }

    if (!Array.isArray(location.units)) return [];

    return [...new Set(location.units.filter(Boolean))].map((unitId) => ({
        type: 'legacy',
        unitId,
        targetId: unitId,
    }));
}

export function getActiveUnitIdsForLocation(location, nowMs = Date.now()) {
    return [
        ...new Set(
            getLocationUnitNavigationTargets(location, nowMs)
                .map((target) => target.unitId)
                .filter(Boolean)
        ),
    ];
}

export function getNavigationTargetForUnitInLocation(location, unitId, nowMs = Date.now()) {
    if (!unitId) return null;
    const unitIdString = String(unitId);
    return getLocationUnitNavigationTargets(location, nowMs)
        .find((target) => String(target.unitId) === unitIdString) ?? null;
}

/**
 * Resolve the concrete `unit_id` that a record belongs to.
 *
 * For legacy units this is just the active `unitId`. For display units the
 * function inspects `unit_ranges`, picks the most-recent range whose
 * timestamp window contains the record, and returns that range's unit_id.
 *
 * Returns `null` when no matching range is found, allowing the caller to
 * skip the record.
 *
 * @param {Object} args
 * @param {Object} args.record - the record to resolve
 * @param {Object} args.currentUnit - the active unit or display unit
 * @param {string} args.unitId - fallback when not a display unit
 * @returns {string|null}
 */
export function resolveRecordUnitId({ record, currentUnit, unitId }) {
    if (!isDisplayUnit(currentUnit)) {
        return unitId ?? null;
    }
    if (!record) return null;
    if (record.unit_id) return record.unit_id;

    const rawTimestamp =
        record.timestamp instanceof Date ? record.timestamp.getTime() : Number(record.timestamp);
    if (!Number.isFinite(rawTimestamp)) return null;

    const ranges = currentUnit.unit_ranges ?? [];
    const matching = [];
    for (const range of ranges) {
        if (!range || !range.unit_id) continue;
        const start = Number(range.start_date);
        if (!Number.isFinite(start) || rawTimestamp < start) continue;

        if (range.current === true) {
            matching.push(range);
            continue;
        }
        const end = Number(range.end_date);
        if (Number.isFinite(end) && rawTimestamp <= end) matching.push(range);
    }
    if (matching.length === 0) return null;
    matching.sort((a, b) => Number(b.start_date) - Number(a.start_date));
    return matching[0].unit_id;
}
